/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { DataZoomComponentOption } from 'echarts';
import type { EChartsInstance, EChartsOption } from 'echarts-for-react';
import ReactECharts from 'echarts-for-react';
import React from 'react';
import {
  EChartsDarkTheme,
  EChartsLightTheme,
  useThemeContext,
} from '@opengeoweb/theme';
import {
  createParameterNames,
  getOption,
  MIN_GRAPH_WIDTH,
  PLOT_HEIGHT,
} from './chartUtils';
import type { PlotWithData } from './types';

// Patch for echarts-for-react ≤ 3.0.6: renderNewEcharts doesn't guard against
// this.ele becoming null when the component unmounts during async init.
interface EChartsReactCoreProto {
  renderNewEcharts?: () => Promise<void>;
  ele?: HTMLElement | null;
}
// Guard: mocked ReactECharts (forwardRef) has no real prototype chain.
const reactEChartsProto = (ReactECharts as { prototype?: unknown }).prototype;
const echartsReactCoreProto: EChartsReactCoreProto | null = reactEChartsProto
  ? (Object.getPrototypeOf(reactEChartsProto) as EChartsReactCoreProto | null)
  : null;
if (echartsReactCoreProto?.renderNewEcharts) {
  const originalRenderNewEcharts = echartsReactCoreProto.renderNewEcharts;
  echartsReactCoreProto.renderNewEcharts =
    async function patchedRenderNewEcharts(
      this: EChartsReactCoreProto & { ele: HTMLElement | null },
    ): Promise<void> {
      try {
        await originalRenderNewEcharts.call(this);
      } catch (err) {
        if (!this.ele) {
          return;
        }
        throw err;
      }
    };
}

interface EChartsLegendApi {
  getModel: () => {
    getComponent: (name: string) => unknown;
  };
  getViewOfComponentModel: (component: unknown) => {
    group: {
      getBoundingRect: () => { height: number };
    };
  };
}

export const useChartHeight = ({
  plotsWithData,
  shouldShowLegend,
  option,
  onLegendHeightChange,
}: {
  plotsWithData: PlotWithData[];
  shouldShowLegend: boolean;
  option: EChartsOption;
  onLegendHeightChange?: (height: number) => void;
}): {
  chartHeight: number;
  chartRef: React.RefObject<ReactECharts | null>;
  updateHeight: (echartsInstance: EChartsInstance) => void;
} => {
  const getDefaultHeight = (plotsWithData: PlotWithData[]): number => {
    return 100 + PLOT_HEIGHT * plotsWithData.length;
  };
  const [chartHeight, setChartHeight] = React.useState(
    getDefaultHeight(plotsWithData),
  );

  const chartRef = React.useRef<ReactECharts>(null!);

  const updateHeight = React.useCallback(
    (echartsInstance: EChartsInstance): void => {
      if (echartsInstance.isDisposed()) {
        return;
      }
      const legendApi = echartsInstance as unknown as EChartsLegendApi;
      if (legendApi.getModel() !== null) {
        const legend = legendApi.getModel().getComponent('legend');
        const legendView = legendApi.getViewOfComponentModel(legend);
        const { height } = legendView.group.getBoundingRect();

        onLegendHeightChange?.(shouldShowLegend ? height : 0);

        const grid = option.grid ?? [];

        const gridArray = Array.isArray(grid) ? grid : [];

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        const newGrid = gridArray.map((grid) => ({
          ...grid,
          top: shouldShowLegend
            ? `${parseInt(grid.top as string, 10) + height}px`
            : grid.top,
        }));

        echartsInstance.setOption({
          legend: {
            top: 0,
            height: shouldShowLegend ? height : 0,
            show: shouldShowLegend,
          },
          grid: newGrid,
        });

        const newHeight =
          getDefaultHeight(plotsWithData) + (shouldShowLegend ? height : 0);

        setChartHeight(newHeight);

        echartsInstance.resize();
      }
    },
    [option.grid, plotsWithData, shouldShowLegend, onLegendHeightChange],
  );

  React.useEffect(() => {
    const instance = chartRef.current?.getEchartsInstance();

    if (instance && !instance.isDisposed()) {
      instance.setOption({
        legend: {
          top: 0,
          show: shouldShowLegend,
        },
      });
      updateHeight(instance);
    }
  }, [shouldShowLegend, updateHeight]);

  React.useEffect(() => {
    const onHeight = (): void => {
      const instance = chartRef.current?.getEchartsInstance();
      if (instance && !instance.isDisposed()) {
        updateHeight(instance);
      }
    };

    window.addEventListener('resize', onHeight);

    return (): void => {
      window.removeEventListener('resize', onHeight);
    };
  }, [updateHeight]);

  return { chartHeight, chartRef, updateHeight };
};

export interface DataZoomState {
  start: number;
  end: number;
}

export const applyDataZoomState = (
  option: EChartsOption,
  dataZoomState: DataZoomState | null,
): EChartsOption => {
  if (!dataZoomState || !option?.dataZoom || !Array.isArray(option.dataZoom)) {
    return option;
  }
  return {
    ...option,
    dataZoom: option.dataZoom.map((dz: DataZoomComponentOption) => ({
      ...dz,
      start: dataZoomState.start,
      end: dataZoomState.end,
    })),
  };
};

export const extractDataZoomState = (params: {
  start?: number;
  end?: number;
  batch?: { start: number; end: number }[];
}): DataZoomState | null => {
  if (params.batch && params.batch.length > 0) {
    return {
      start: params.batch[0].start,
      end: params.batch[0].end,
    };
  }
  if (params.start !== undefined && params.end !== undefined) {
    return {
      start: params.start,
      end: params.end,
    };
  }
  return null;
};

export const TimeSeriesChart: React.FC<{
  plotsWithData: PlotWithData[];
  animation?: boolean;
  shouldShowLegend?: boolean;
  onToggleLegendParam?: (legendId: string) => void;
  onLegendHeightChange?: (height: number) => void;
}> = ({
  plotsWithData,
  animation = false,
  shouldShowLegend = true,
  onToggleLegendParam,
  onLegendHeightChange,
}) => {
  const { namedTheme } = useThemeContext();
  const isDark = namedTheme?.theme.palette.mode === 'dark';

  const dataZoomStateRef = React.useRef<DataZoomState | null>(null);

  const lastLegendEventRef = React.useRef<unknown>(null);

  const enabledPlots = React.useMemo(
    () =>
      plotsWithData.filter(
        (plot) => plot.enabled === undefined || plot.enabled,
      ),
    [plotsWithData],
  );

  const option = React.useMemo(
    () => getOption(enabledPlots, shouldShowLegend),
    [enabledPlots, shouldShowLegend],
  );

  const optionWithZoom = React.useMemo(
    (): EChartsOption => applyDataZoomState(option, dataZoomStateRef.current),
    [option],
  );

  const { chartHeight, chartRef, updateHeight } = useChartHeight({
    plotsWithData: enabledPlots,
    shouldShowLegend,
    option: optionWithZoom,
    onLegendHeightChange,
  });

  return option ? (
    <div data-testid="TimeSeriesChart">
      <ReactECharts
        notMerge={true}
        onChartReady={updateHeight}
        // noop, prevent flickering when toggling legend
        onEvents={{
          finished: () => {},
          datazoom: (params: {
            start?: number;
            end?: number;
            batch?: { start: number; end: number }[];
          }) => {
            const newState = extractDataZoomState(params);
            if (newState) {
              dataZoomStateRef.current = newState;
            }
          },
          legendselectchanged: (params: {
            name: string;
            selected: Record<string, boolean>;
          }) => {
            if (!option.legend) {
              return;
            }

            // Ignore duplicate deliveries of the same emit (see ref comment).
            if (lastLegendEventRef.current === params) {
              return;
            }
            lastLegendEventRef.current = params;

            const foundParam = createParameterNames(plotsWithData).find(
              (param) => param.name === params.name,
            );

            if (foundParam?.id) {
              onToggleLegendParam?.(foundParam.id);
            }
          },
        }}
        option={{
          ...optionWithZoom,
          animation,
        }}
        ref={chartRef}
        style={{
          background: 'white',
          height: chartHeight,
          minWidth: MIN_GRAPH_WIDTH,
        }}
        theme={isDark ? EChartsDarkTheme : EChartsLightTheme}
      />
    </div>
  ) : null;
};
